// PaymentNote.tsx — the bookkeeper's note on one confirmed invoice.
//
// Open / Partial / Paid is HUMAN-SET by design (Mannie, 2026-08-24): the actual
// payments live in QuickBooks, so her word is the source and there is nothing
// to derive it from. Overdue is the one derived idea, and it lives in
// isOverdue(), never here and never in a column.
//
// The hold is its own flag: "do not pay this yet", with a reason that travels
// with it. Arming asks for the reason first because the server refuses a bare
// hold; releasing clears the reason with it. Past holds survive in the edit
// trail, server-side.
//
// STATE CONTRACT, earned in the adversary round: `save` RETURNS the server's
// row and every call site hands it to onSaved in the same synchronous block as
// its own local-state updates, so React batches them into one commit and the
// controls never flicker through a stale frame. The select wears a `pending`
// value while its PATCH is in flight — a controlled select with no local state
// snaps back to the old value the instant React re-renders, shows the opposite
// of what was clicked for the whole round trip, and (worse) makes each
// arrow-key press on a closed select fire a write against the unchanged value,
// so Paid is unreachable by keyboard. Pending clears on settle either way: on
// success the parent's fresh row carries the value; on refusal the snap-back
// is the honest answer, beside the toast that says why.
//
// One component, two homes: the PO page's billing rail and the invoice detail
// page (which also gets the due-date field, the one header fact that
// legitimately changes after confirm). Both are can_financial surfaces, so
// this component never checks capability itself.

import React, { useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import { buttonClass } from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Reveal from '../components/ui/Reveal';
import {
  useApInvoicesApi,
  PAYMENT_STATUS_LABELS,
  type PaymentStatus,
  type VendorInvoice,
} from './apInvoicesApi';

export interface PaymentNoteSubject {
  id: string;
  invoice_number: string | null;
  payment_status: PaymentStatus;
  payment_on_hold: boolean;
  payment_hold_reason: string | null;
  due_date: string | null;
}

const PaymentNote: React.FC<{
  invoice: PaymentNoteSubject;
  /** The invoice detail page shows the due-date field; the rail keeps the date
   *  read-only and sends people to the invoice to change it. */
  showDueDate?: boolean;
  /** Receives the server's own row. The parent folds it into its state in the
   *  same tick, which is what keeps these controls truthful without a second
   *  network trip. */
  onSaved: (invoice: VendorInvoice) => void;
}> = ({ invoice, showDueDate, onSaved }) => {
  const api = useApInvoicesApi();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Checking Hold arms it: the reason comes first, then the hold, one request.
  const [arming, setArming] = useState(false);
  const [reasonDraft, setReasonDraft] = useState('');
  const [dueDraft, setDueDraft] = useState<string | null>(null);
  // The select's in-flight value; null = show the stored fact.
  const [pending, setPending] = useState<PaymentStatus | null>(null);

  // Two unnumbered invoices on one screen must not share an accessible name.
  const label = invoice.invoice_number || `invoice ${invoice.id.slice(0, 8)}`;

  /** One request, the row or null. Call sites pair onSaved with their own
   *  local updates in one synchronous block, so the commit is atomic. */
  const save = async (
    fields: Parameters<typeof api.setPaymentState>[1],
  ): Promise<VendorInvoice | null> => {
    setBusy(true);
    try {
      const res = await api.setPaymentState(invoice.id, fields);
      return res.invoice;
    } catch (e: any) {
      toast.error(e?.data?.error?.message || e?.message || 'That note was refused.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          label={`Payment status for ${label}`}
          size="sm"
          width="fit"
          value={pending ?? invoice.payment_status}
          disabled={busy}
          options={(Object.keys(PAYMENT_STATUS_LABELS) as PaymentStatus[]).map((k) => ({ key: k, label: PAYMENT_STATUS_LABELS[k] }))}
          onChange={async (k) => {
            const next = k as PaymentStatus;
            setPending(next);
            const updated = await save({ payment_status: next });
            if (updated) onSaved(updated);
            setPending(null);
          }}
        />

        <Checkbox
          size="sm"
          label="Hold"
          checked={invoice.payment_on_hold || arming}
          disabled={busy}
          onChange={async (e) => {
            if (e.target.checked) {
              setArming(true);
              return;
            }
            if (!invoice.payment_on_hold) {
              setArming(false);
              return;
            }
            const updated = await save({ payment_on_hold: false });
            if (updated) {
              onSaved(updated);
              setArming(false);
            }
          }}
        />

        {showDueDate && (
          <label className="flex items-center gap-1.5 text-xs text-on-surface-variant">
            <span>Due</span>
            <Field
              type="date"
              size="sm"
              className="!w-auto"
              value={dueDraft ?? invoice.due_date ?? ''}
              disabled={busy}
              aria-label={`Due date for ${label}`}
              onChange={(e) => setDueDraft(e.target.value)}
              onBlur={async () => {
                const next = dueDraft === '' ? null : dueDraft;
                if (dueDraft === null || next === invoice.due_date) {
                  setDueDraft(null);
                  return;
                }
                const updated = await save({ due_date: next });
                // Cleared either way: success shows the fresh row, refusal
                // snaps back to the stored date beside the toast — a draft
                // that outlives its failure reads as saved when it is not.
                if (updated) onSaved(updated);
                setDueDraft(null);
              }}
            />
          </label>
        )}
      </div>

      <Reveal show={arming && !invoice.payment_on_hold}>
        <div className="flex items-center gap-2 flex-wrap">
          <Field
            type="text"
            size="sm"
            className="flex-1 min-w-[12rem] w-auto"
            value={reasonDraft}
            placeholder="Why is payment held? The next person reads this."
            aria-label={`Hold reason for ${label}`}
            autoFocus
            onChange={(e) => setReasonDraft(e.target.value)}
          />
          <button
            type="button"
            disabled={busy || !reasonDraft.trim()}
            className={buttonClass('quiet', 'sm')}
            onClick={async () => {
              const updated = await save({
                payment_on_hold: true,
                payment_hold_reason: reasonDraft.trim(),
              });
              if (updated) {
                // One synchronous block with onSaved: the parent's fresh row
                // (hold now true) and the disarm land in one React commit, so
                // the checkbox never blinks through an unchecked frame.
                onSaved(updated);
                setArming(false);
                setReasonDraft('');
              }
            }}
          >
            Hold payment
          </button>
          <button
            type="button"
            disabled={busy}
            className={buttonClass('quiet', 'sm')}
            onClick={() => { setArming(false); setReasonDraft(''); }}
          >
            Never mind
          </button>
        </div>
      </Reveal>

      <Reveal show={!!(invoice.payment_on_hold && invoice.payment_hold_reason)}>
        <p className="text-xs text-amber-300/90">Held: {invoice.payment_hold_reason}</p>
      </Reveal>
    </div>
  );
};

export default PaymentNote;
